'use client';

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  MapPin,
  Search,
} from 'lucide-react';

/**
 * Address -> carriers -> registered opportunity.
 *
 * The lookup is the trade: carriers do not publish serviceability, so
 * answering "who can actually serve this building" is worth a real address.
 * Everything below the results exists to turn that address into something we
 * can register in Loc8 the same day.
 *
 * DESIGN RULE: the form stays reachable no matter what the lookup does. A
 * failed FCC call, a zero-provider block and an unmatched address are all
 * routed to the same "send it to us and we'll check by hand" state, because a
 * submitted address is worth more than a rendered provider list.
 */

interface ProviderService {
  technology: string;
  kind: string;
  maxDownMbps: number | null;
  maxUpMbps: number | null;
}

interface Provider {
  name: string;
  brand?: string;
  bestKind: string;
  services: ProviderService[];
  quotable: boolean;
}

interface LookupResult {
  matchedAddress: string;
  dataVintage: string;
  providers: Provider[];
}

const KIND_STYLES: Record<string, { dot: string; label: string }> = {
  fiber: { dot: 'bg-emerald-500', label: 'Fiber' },
  cable: { dot: 'bg-blue-500', label: 'Cable' },
  copper: { dot: 'bg-amber-500', label: 'Copper' },
  wireless: { dot: 'bg-purple-500', label: 'Fixed wireless' },
  satellite: { dot: 'bg-gray-400', label: 'Satellite' },
  other: { dot: 'bg-gray-400', label: 'Other' },
};

const INTERESTS = [
  { value: 'internet', label: 'Business internet / circuits' },
  { value: 'phone-system', label: 'Phone system (UCaaS)' },
  { value: 'contact-center', label: 'Contact center (CCaaS)' },
  { value: 'sd-wan', label: 'SD-WAN / managed network' },
  { value: 'multi-site', label: 'Multi-site rollout' },
];

function speedLabel(s: ProviderService): string {
  if (s.maxDownMbps === null) {
    // Dedicated and CIR-priced business services report 0 in Form 477. That is
    // "priced per deal", not "no service" — printing 0 Mbps would be wrong.
    return 'Dedicated / priced per site';
  }
  const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 ? 1 : 0)} Gbps` : `${n} Mbps`);
  return s.maxUpMbps ? `${fmt(s.maxDownMbps)} / ${fmt(s.maxUpMbps)}` : fmt(s.maxDownMbps);
}

export default function BusinessInternetCheck() {
  const [address, setAddress] = useState('');
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [lookupError, setLookupError] = useState('');
  const [result, setResult] = useState<LookupResult | null>(null);

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    additionalSites: '',
    currentProvider: '',
    monthlySpend: '',
    contractEnd: '',
    seatCount: '',
    message: '',
  });
  const [interests, setInterests] = useState<string[]>(['internet']);
  const [honeypot, setHoneypot] = useState('');
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function runLookup(e: React.FormEvent) {
    e.preventDefault();
    if (address.trim().length < 8) {
      setLookupError('Enter a full street address, including city and state or ZIP.');
      setLookupState('error');
      return;
    }
    setLookupState('loading');
    setLookupError('');
    try {
      const res = await fetch('/api/network-availability', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLookupError(data.error || 'That lookup did not work.');
        setLookupState('error');
        return;
      }
      setResult(data);
      // Adopt the geocoder's normalised address as the editable value. Binding
      // the field to `matchedAddress || address` instead would render a value
      // that ignores its own onChange, making the address uneditable after a
      // lookup — which is exactly when someone needs to correct a suite number.
      if (data.matchedAddress) setAddress(data.matchedAddress);
      setLookupState('done');
    } catch {
      setLookupError('The lookup could not be reached. Send us the address and we will run it by hand.');
      setLookupState('error');
    }
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (honeypot) return;
    setFormState('submitting');
    setFormError('');

    const [firstName, ...rest] = form.fullName.trim().split(/\s+/);
    try {
      const res = await fetch('/api/network-opportunity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName: rest.join(' ') || undefined,
          email: form.email,
          company: form.company,
          phone: form.phone || undefined,
          serviceAddress: address,
          additionalSites: form.additionalSites || undefined,
          currentProvider: form.currentProvider || undefined,
          monthlySpend: form.monthlySpend || undefined,
          contractEnd: form.contractEnd || undefined,
          seatCount: form.seatCount ? Number.parseInt(form.seatCount, 10) : undefined,
          interests,
          message: form.message || undefined,
          providers: result?.providers.map((p) => ({ name: p.name, bestKind: p.bestKind })),
          sourcePage: typeof window !== 'undefined' ? window.location.pathname : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Submission failed.');
        setFormState('error');
        return;
      }
      setFormState('success');
    } catch {
      setFormError('Submission failed. Please try again.');
      setFormState('error');
    }
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  const inputClass =
    'w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors';
  const labelClass =
    'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  if (formState === 'success') {
    return (
      <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          We&apos;re on it
        </h2>
        <p className="text-gray-700 dark:text-gray-300 max-w-lg mx-auto">
          We&apos;ll run your address through the carrier serviceability tools and come
          back with what&apos;s genuinely available, with real pricing — usually within
          one business day. No sales calls from carriers; we do the talking.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* ---- Step 1: the lookup ---- */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6 md:p-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
          Check an address
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          See which providers report business service at your location, from public
          FCC filings. Instant, no email required.
        </p>

        <form onSubmit={runLookup} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="1200 Main St, Kansas City, MO 64105"
              aria-label="Business street address"
              className={`${inputClass} pl-11`}
            />
          </div>
          <button
            type="submit"
            disabled={lookupState === 'loading'}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#2813e8] hover:bg-[#1f0fb8] disabled:opacity-60 text-white font-semibold transition-colors"
          >
            {lookupState === 'loading' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            Check address
          </button>
        </form>

        {lookupState === 'error' && (
          <div className="mt-4 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              {lookupError} You can still send it below and we&apos;ll check it against
              the carrier tools by hand.
            </p>
          </div>
        )}

        {lookupState === 'done' && result && (
          <div className="mt-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Matched to <span className="font-medium text-gray-900 dark:text-white">{result.matchedAddress}</span>
            </p>

            {result.providers.length === 0 ? (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-gray-700 dark:text-gray-300">
                No provider filed business service for this census block. That is
                common for new construction, rural blocks and business parks — and
                it is frequently wrong. The carrier tools we use are current;
                send the address below and we&apos;ll check properly.
              </div>
            ) : (
              <>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {result.providers.map((p) => {
                    const style = KIND_STYLES[p.bestKind] || KIND_STYLES.other;
                    return (
                      <li
                        key={p.name}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white leading-tight">
                              {p.brand || p.name}
                            </p>
                            {p.brand && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">{p.name}</p>
                            )}
                          </div>
                          <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${style.dot}`} />
                        </div>
                        <ul className="space-y-1">
                          {p.services.slice(0, 3).map((s, i) => (
                            <li key={i} className="text-sm text-gray-600 dark:text-gray-400">
                              {s.technology} · {speedLabel(s)}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>

                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  Source: {result.dataVintage}, filtered to providers reporting
                  business service. This data is self-reported by carriers, recorded
                  at census-block level rather than per building, and predates recent
                  fiber builds — so treat it as a starting point. Confirming what
                  actually delivers to your suite, and at what price, is the next
                  step below.
                </p>
              </>
            )}
          </div>
        )}
      </section>

      {/* ---- Step 2: the value prop ---- */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 p-6 md:p-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Why go through us instead of calling the carrier?
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          It&apos;s a fair question, and the answer is mostly about who pays us.
        </p>
        <dl className="grid gap-6 md:grid-cols-2">
          <div>
            <dt className="font-semibold text-gray-900 dark:text-white mb-1">
              It doesn&apos;t cost you anything
            </dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">
              Carriers pay us out of their channel budget — the same budget that
              funds their own sales team. Nothing is added to your bill, and the
              pricing we bring back is carrier list or better. You are not paying
              a middleman; you are using the carrier&apos;s indirect channel instead
              of their direct one.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900 dark:text-white mb-1">
              One process instead of five
            </dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">
              Going direct means five carriers, five reps, five quote formats and
              five follow-up cadences, most of which start with a serviceability
              check they will not run until you have sat through a discovery call.
              We run them in parallel and hand you one comparison.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900 dark:text-white mb-1">
              Nobody here is on one carrier&apos;s payroll
            </dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">
              A Comcast rep will confirm Comcast is available. They will not
              mention that Lumen lit the building last year. Our commission is
              within roughly a point and a half across the major carriers, so
              steering you costs us as much as it gains — the incentive is to get
              it right and keep the account.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900 dark:text-white mb-1">
              We know which line items to argue with
            </dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">
              Special construction charges that appear after you sign. Evergreen
              auto-renewal clauses. Early termination liability calculated on the
              full remaining term. Whether you actually need a dedicated circuit
              or whether business coax is fine for what you do. These are the
              things that cost real money, and none of them are on the quote.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900 dark:text-white mb-1">
              The circuit and the phone system are one project
            </dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">
              Number porting, E911 registration and cutover sequencing are where
              these go wrong, and they go wrong at the seam between two vendors
              who each say it is the other&apos;s problem. We hold both ends.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-900 dark:text-white mb-1">
              You keep the quotes either way
            </dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">
              If you take what we find and sign direct, that is genuinely fine.
              There is no fee, no exclusivity and nothing to sign to get the
              comparison.
            </dd>
          </div>
        </dl>
      </section>

      {/* ---- Step 3: the capture ---- */}
      <section
        id="get-quotes"
        className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6 md:p-8"
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
          Get real pricing for this address
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          We&apos;ll check live carrier serviceability, pull pricing for what actually
          delivers there, and send you a comparison. No carrier sales calls.
        </p>

        <form onSubmit={submitForm} className="space-y-5">
          {/* Honeypot */}
          <div className="absolute w-px h-px -m-px overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="serviceAddress" className={labelClass}>
              Service address <span className="text-red-500">*</span>
            </label>
            <input
              id="serviceAddress"
              type="text"
              required
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street, city, state, ZIP"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="additionalSites" className={labelClass}>
              Additional locations{' '}
              <span className="text-gray-400 font-normal">(optional, one per line)</span>
            </label>
            <textarea
              id="additionalSites"
              name="additionalSites"
              rows={2}
              value={form.additionalSites}
              onChange={handleChange}
              placeholder="Checking several sites at once takes us no longer than one"
              className={`${inputClass} resize-y`}
            />
          </div>

          <fieldset>
            <legend className={labelClass}>What do you need?</legend>
            <div className="flex flex-wrap gap-2 mt-1">
              {INTERESTS.map((opt) => {
                const active = interests.includes(opt.value);
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() =>
                      setInterests(
                        active
                          ? interests.filter((i) => i !== opt.value)
                          : [...interests, opt.value]
                      )
                    }
                    aria-pressed={active}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                      active
                        ? 'bg-[#2813e8] border-[#2813e8] text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="currentProvider" className={labelClass}>
                Current provider{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="currentProvider"
                name="currentProvider"
                type="text"
                value={form.currentProvider}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              {/* The highest-value field on the form: these only get won near
                  the end date, and knowing it turns a "no" into a calendar
                  entry. */}
              <label htmlFor="contractEnd" className={labelClass}>
                When does your contract end?{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="contractEnd"
                name="contractEnd"
                type="text"
                value={form.contractEnd}
                onChange={handleChange}
                placeholder="e.g. March 2027, or not sure"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="monthlySpend" className={labelClass}>
                Current monthly spend{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="monthlySpend"
                name="monthlySpend"
                type="text"
                value={form.monthlySpend}
                onChange={handleChange}
                placeholder="Helps us benchmark"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="seatCount" className={labelClass}>
                Users at this site{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="seatCount"
                name="seatCount"
                type="number"
                min={1}
                value={form.seatCount}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="fullName" className={labelClass}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={form.fullName}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="company" className={labelClass}>
                Company <span className="text-red-500">*</span>
              </label>
              <input
                id="company"
                name="company"
                type="text"
                required
                value={form.company}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="email" className={labelClass}>
                Work email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="phone" className={labelClass}>
                Phone <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className={labelClass}>
              Anything else{' '}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={3}
              value={form.message}
              onChange={handleChange}
              placeholder="Move-in date, current pain, redundancy requirements…"
              className={`${inputClass} resize-y`}
            />
          </div>

          {formState === 'error' && (
            <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{formError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={formState === 'submitting'}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg bg-[#2813e8] hover:bg-[#1f0fb8] disabled:opacity-60 text-white font-semibold transition-colors"
          >
            {formState === 'submitting' && <Loader2 className="w-5 h-5 animate-spin" />}
            Send me the comparison
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            We&apos;ll only use this to check availability and pricing for you. No
            carrier gets your details until you tell us to proceed.
          </p>
        </form>
      </section>
    </div>
  );
}
